import { m } from '$lib/paraglide/messages';
import { AssetType } from '$lib/utils/asset';
import type { ImageSrc } from './image-src';
import type { Tag } from './tag';
import { assetLink } from '$lib/utils/asset';
import { PUBLIC_URL } from '$env/static/public';

export class Track {
  id: number;
  newSlug: string;
  title: string;
  slug: string;
  image: string;
  srcset: ImageSrc[];
  description: string;
  mp3: string;
  duration: string;
  tempo: string;
  versions: string;
  sales: string;
  comments: string;
  filename: string;

  type: AssetType;
  mainSlug: string;

  prices: Array<Price>;
  tags: Array<Tag>;
  genres: Array<Tag>;
  moods: Array<Tag>;
  energyLevels: Array<Tag>;
  instruments: Array<Tag>;
  themes: Array<Tag>;
  types: Array<Tag>;
  categories: Array<Tag>; // SFX categories

  mainGenre: Tag;
  mainCategory: Tag;

  isPremium: boolean;
  isNew: boolean;
  isSoundEffect: boolean;
  hidePlayer: boolean;

  waveform: number[];
  additionalTracks: TrackAdditionalTracks;
  // Tracks that were used for an assistant generation.
  // This will be used to exclude them from the recommendations when regenerating.
  excludeTracks: number[];
  assistantSuggestions: string[];
  mp3Versions: Track[];
  mp3MainVersion: Track;

  shadow: string;

  public static GetSchema(track: Track, langHref: (path: string) => string) {
    let path = assetLink(langHref, track, track.type);
    if (path.charAt(0) === '/') {
      path = path.substr(1);
    }
    const splitDuration = track.duration?.split(':');
    let durationISO = 'T0M0S';
    if (splitDuration?.length > 1) {
      durationISO = 'T' + splitDuration[0] + 'M' + splitDuration[1] + 'S';
    }
    return {
      '@context': 'https://schema.org/',
      '@type': 'MusicRecording',
      '@id': PUBLIC_URL + path,
      name: track.title,
      producer: {
        '@type': 'Organization',
        name: 'HookSounds',
      },
      duration: durationISO, // ISO FORMAT
      recordingOf: {
        '@type': 'MusicComposition',
        name: track.title,
      },
      acquireLicensePage: PUBLIC_URL + path,
      audio: PUBLIC_URL + path,
      offers: track.prices?.map((p) => {
        return {
          '@type': 'Offer',
          name: p.name,
          priceCurrency: 'USD',
          price: p.price,
          identifier: p.id,
          availability: 'https://schema.org/InStock',
        };
      }),
    };
  }

  public static GetBreadcrumb(track: Track, assetType: AssetType, locale = 'en') {
    let name = '';
    let url = '';
    if (assetType === AssetType.SFX) {
      name = 'Sound Effects';
      url = 'https://www.hooksounds.com/sound-effects/';
      if (locale === 'es') {
        name = 'Efectos De Sonido';
        url = 'https://www.hooksounds.com/es/sound-effects/';
      } else if (locale === 'de') {
        name = 'Soundeffekte';
        url = 'https://www.hooksounds.com/de/sound-effects/';
      } else if (locale === 'pt-br') {
        name = 'Efeitos Sonoros';
        url = 'https://www.hooksounds.com/pt-br/sound-effects/';
      }
    } else if (assetType === AssetType.Intro) {
      name = 'Intros';
      url = 'https://www.hooksounds.com/intros/';
      if (locale === 'es') {
        url = 'https://www.hooksounds.com/es/intros/';
      } else if (locale === 'de') {
        url = 'https://www.hooksounds.com/de/intros/';
      } else if (locale === 'pt-br') {
        url = 'https://www.hooksounds.com/pt-br/intros/';
      }
    } else if (assetType === AssetType.Track) {
      name = 'Royalty Free Music';
      url = 'https://www.hooksounds.com/royalty-free-music/';
      if (locale === 'es') {
        name = 'Música Libre De Regalías';
        url = 'https://www.hooksounds.com/es/royalty-free-music/';
      } else if (locale === 'de') {
        name = 'GEMAfreie Musik';
        url = 'https://www.hooksounds.com/de/royalty-free-music/';
      } else if (locale === 'pt-br') {
        name = 'Musica Livre De Direitos';
        url = 'https://www.hooksounds.com/pt-br/royalty-free-music/';
      }
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name,
          item: url,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: track.title,
          item: url + track.slug + '/' + track.id + '/',
        },
      ],
    };
  }
}

export class Price {
  id: number;
  name: string;
  amount: string;
  price: string;
  salePrice: number;
  regularPrice: number;
  default: boolean;
}

export function getPriceName(name: string | undefined | null) {
  if (!name) {
    return '';
  }
  name = name.toLowerCase();
  if (name.includes('mention')) {
    return m['common.license-name.use-mention']();
  } else if (name.includes('pro')) {
    return m['common.license-name.pro']();
  } else if (name.includes('business')) {
    return m['common.license-name.business']();
  } else if (name.includes('broadcast')) {
    return m['common.license-name.broadcast']();
  } else if (name.includes('license')) {
    return m['common.license.sfx']();
  } else {
    return name;
  }
}

export function getPriceFeatures(name: string | undefined | null): string[] {
  if (!name) {
    return [];
  }
  name = name.toLowerCase();
  let featuresString = '';
  if (name.includes('mention')) {
    featuresString = m['download-modal.features.use-mention']();
  } else if (name.includes('pro')) {
    featuresString = m['download-modal.features.pro']();
  } else if (name.includes('business')) {
    featuresString = m['download-modal.features.business']();
  } else if (name.includes('broadcast')) {
    featuresString = m['download-modal.features.broadcast']();
  } else if (name.includes('license')) {
    featuresString = m['download-asset-modal.single-sfx-features']();
  } else {
    featuresString = name;

  }
  return featuresString.split('|');
}


export class TrackAdditionalTracks {
  similar: Track[];
}

export class AssetVersion {
  id: number;
  name: string;
  url: string;
  duration: string;
  waveform: number[];
}
